import { Menu, Portal } from '@chakra-ui/react'
import cn from 'clsx'
import { FC, useState } from 'react'

import Plus from '@/assets/icons/plus.svg'

import { LANGUAGES } from '@/constants/languages'

import { Button, Typography } from '../../../../components/ui'
import { ITranslateParams } from '../../File'
import { useFile } from '../../hooks/useFile'
import { TLangCodes } from '../TextContainer'

interface ILanguageBlockProps {
	setIsTranslateLoading: (arg: boolean) => void
	onTranslate: () => Promise<unknown>
	disabled?: boolean
	setTab: (value: string) => void
	language: string[]
	languagesCodes: TLangCodes[]
	tab: string | null
	setRightTab: (value: string) => void
	originalLang: string
	setIsChoosingLanguage: (value: boolean) => void
	setTargetLang: (value: string) => void
	setIsTranslateError: (value: boolean) => void
	setLastTranslateParams: (params: ITranslateParams) => void
	originalSourceName: string
	type?: 'original' | 'translation'
}

const LanguageBlock: FC<ILanguageBlockProps> = ({
	setIsTranslateLoading,
	onTranslate,
	disabled,
	setTab,
	language,
	languagesCodes,
	tab,
	setRightTab,
	originalLang,
	setIsChoosingLanguage,
	setTargetLang,
	setIsTranslateError,
	setLastTranslateParams,
	originalSourceName,
	type
}) => {
	const { translateShotlist } = useFile()

	const [isMenuOpen, setIsMenuOpen] = useState(false)

	const translations = Array.from(
		new Set(language.filter(item => item !== 'original'))
	)

	const currentTranslation =
		tab !== 'original' && translations.includes(tab)
			? tab
			: translations[0] || null

	const hasMultipleTranslations = translations.length > 1

	const translate = async (targetLang: string, sourceLang?: string) => {
		try {
			setIsTranslateLoading(true)
			setIsTranslateError(false)

			const actualSourceLang = sourceLang || originalLang

			setLastTranslateParams({
				targetLang,
				sourceLang: actualSourceLang
			})

			await translateShotlist({
				target_language: targetLang,
				source_language: actualSourceLang
			})

			setRightTab(targetLang)
			await onTranslate()
		} catch (error) {
			console.error('Error:', error)
			setIsTranslateError(true)
		} finally {
			setIsTranslateLoading(false)
		}
	}

	const handleTranslate = (lang: 'en' | 'ru' | 'ar' | 'es') => {
		if (language.includes(lang)) {
			setTab(lang)
			return
		}

		if (lang === originalLang) {
			setTab('original')
			return
		}

		// If there is not any translation, take original language
		if (language.length === 1) {
			translate(lang)
			return
		}

		// If there is at least one translation - open menu for choosing source language
		setTargetLang(lang)
		setIsChoosingLanguage(true)
	}

	const handleSwitchTranslation = (lang: string) => {
		setTab(lang)
		setIsMenuOpen(false)
	}

	const handleTranslationTabClick = () => {
		// If there are less then 2 translations - just activate tab
		if (!hasMultipleTranslations) {
			setTab(currentTranslation)
			return
		}

		// If it's not active - just activate
		if (tab !== currentTranslation) {
			setTab(currentTranslation)
			setIsMenuOpen(false)
			return
		}

		// If tab is active - open/close translations menu
		setIsMenuOpen(prev => !prev)
	}

	const translationsTab =
		translations.length > 1 ? 'Translations' : LANGUAGES[translations[0]]

	return (
		<div className='flex'>
			{/* Original language block */}
			{type === 'original' && (
				<div className='py-4 w-full bg-gray-50'>
					<Typography
						variant='text-semibold'
						className='text-center !text-[12px] !font-[500] !leading-[16px]'
					>
						{originalSourceName}
					</Typography>
				</div>
			)}

			{/* Translations block */}
			{currentTranslation && type === 'translation' && (
				<Menu.Root
					open={isMenuOpen}
					onOpenChange={details => setIsMenuOpen(details.open)}
					positioning={{ placement: 'top' }}
				>
					<Menu.Trigger asChild>
						<Button
							disabled={disabled}
							className={cn(
								'w-full !text-black !border-[1px] rounded-none hover:bg-gray-50 flex items-center justify-center gap-1',
								tab === currentTranslation ? 'bg-gray-50' : ''
							)}
							onClick={handleTranslationTabClick}
						>
							<span
								className={cn(
									'flex items-center gap-1 !text-[12px] !font-[500] !leading-[20px]',
									hasMultipleTranslations && tab === currentTranslation
										? 'text-blue-500'
										: 'text-black'
								)}
							>
								{translationsTab}
							</span>
						</Button>
					</Menu.Trigger>

					{/* If there are more then one translations - show menu */}
					{hasMultipleTranslations && (
						<Portal>
							<Menu.Positioner className='ml-4'>
								<Menu.Content>
									{translations.map((lang, ind) => (
										<Menu.Item
											key={lang + ind}
											value={lang}
											onClick={() => handleSwitchTranslation(lang)}
										>
											{LANGUAGES[lang]}
										</Menu.Item>
									))}
								</Menu.Content>
							</Menu.Positioner>
						</Portal>
					)}
				</Menu.Root>
			)}

			{type === 'translation' && (
				<Menu.Root positioning={{ placement: 'top' }}>
					<Menu.Trigger asChild>
						<Button
							disabled={disabled}
							className='w-full !text-blue-500 !border-[1px] py-[13px] rounded-none hover:bg-gray-50'
							size='lg'
						>
							<span className='flex items-center gap-1 !text-[12px] !font-[500] !leading-[20px]'>
								<Plus />
								Add Translation
							</span>
						</Button>
					</Menu.Trigger>
					<Portal>
						<Menu.Positioner className='ml-4'>
							<Menu.Content>
								{languagesCodes.map(val => (
									<Menu.Item
										key={val}
										value={val}
										onClick={() => handleTranslate(val)}
									>
										{LANGUAGES[val]}
									</Menu.Item>
								))}
							</Menu.Content>
						</Menu.Positioner>
					</Portal>
				</Menu.Root>
			)}
		</div>
	)
}

export default LanguageBlock
